import { assertPLCCapabilityManifest } from "./plcCapabilityManifest";

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";
const PUBLISH_URL = _gwConfig.PUBLISH_URL || process.env.REACT_APP_PUBLISH_URL || "http://localhost:4000";
// Control Seat website URL — used by online license activation to open a
// popup that calls /api/licenses/issue using the user's session cookies
// and posts the resulting license file back via window.postMessage.
export const CONTROLSEAT_URL =
  _gwConfig.CONTROLSEAT_URL ||
  process.env.REACT_APP_CONTROLSEAT_URL ||
  "https://controlseat.com";

async function request(path, options = {}) {
  const { headers: optionHeaders, ...requestOptions } = options;
  const res = await fetch(`${CORE_URL}${path}`, {
    ...requestOptions,
    headers: { "Content-Type": "application/json", ...optionHeaders },
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text();
    const error = new Error(text || `${res.status} ${res.statusText}`);
    error.status = res.status;
    // Some endpoints answer with a structured body the caller has to act on —
    // /api/agent/conversations reports a seq collision as 409 {error, nextSeq}
    // and the client retries at nextSeq. Attaching the parsed body means that
    // caller reads a number instead of re-parsing an Error message.
    try {
      error.body = JSON.parse(text);
    } catch {}
    throw error;
  }
  if (res.status === 204) return null;
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(
      "The gateway API returned the web app instead of JSON. Restart driverhost from this checkout so the latest backend routes are loaded."
    );
  }
  return res.json();
}

export function listDatasources() {
  return request("/api/datasources");
}

export function createDatasource(payload) {
  return request("/api/datasources", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function pingDatasource(name) {
  return request(`/api/datasources/${encodeURIComponent(name)}/ping`, {
    method: "POST",
  });
}

export function updateDatasource(name, payload) {
  return request(`/api/datasources/${encodeURIComponent(name)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteDatasource(name) {
  return request(`/api/datasources/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}

// Machine Health commissioning data used by the edge receiver workflow. The
// Gateway only selects an already configured machine/sensor pair; machine
// setup and diagnostic policy remain in Intelligence → Machine Health.
export function listMachineHealthAssets(options = {}) {
  return request("/api/machine-health/assets", options);
}

export function listMachineHealthSources(options = {}) {
  return request("/api/machine-health/sources", options);
}

export function listDiscoveredMachineHealthSensors(options = {}) {
  const { gatewayId, connectorId, ...requestOptions } = options;
  const params = new URLSearchParams();
  if (gatewayId && connectorId) {
    params.set("gateway_id", gatewayId);
    params.set("connector_id", connectorId);
  }
  const query = params.toString();
  return request(`/api/machine-health/discovered-sensors${query ? `?${query}` : ""}`, requestOptions);
}

export function renameDiscoveredMachineHealthSensor(sensorKey, displayName, expectedDisplayName) {
  return request(`/api/machine-health/discovered-sensors/${encodeURIComponent(sensorKey)}`, {
    method: "PATCH",
    body: JSON.stringify({
      display_name: displayName,
      expected_display_name: expectedDisplayName,
    }),
  });
}

export function retireDiscoveredMachineHealthSensor(sensorKey, gatewayId, connectorId) {
	const params = new URLSearchParams({ gateway_id: gatewayId, connector_id: connectorId });
	return request(`/api/machine-health/discovered-sensors/${encodeURIComponent(sensorKey)}?${params.toString()}`, {
    method: "DELETE",
  });
}

export function listSchemas(name, options = {}) {
  const params = new URLSearchParams();
  if (options.includeSystem === true) {
    params.set("include_system", "true");
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return request(`/api/datasources/${encodeURIComponent(name)}/schema${suffix}`);
}

export function listTables(name, schema) {
  return request(
    `/api/datasources/${encodeURIComponent(name)}/schema/${encodeURIComponent(schema)}/tables`
  );
}

export function listColumns(name, schema, table) {
  return request(
    `/api/datasources/${encodeURIComponent(name)}/schema/${encodeURIComponent(schema)}/tables/${encodeURIComponent(table)}/columns`
  );
}

// ── API Profiles ──────────────────────────────────────────────────────

export function listApiProfiles() {
  return request("/api/apiprofiles");
}

export function createApiProfile(payload) {
  return request("/api/apiprofiles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateApiProfile(id, payload) {
  return request(`/api/apiprofiles/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteApiProfile(id) {
  return request(`/api/apiprofiles/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function listProfileTags(id) {
  return request(`/api/apiprofiles/${encodeURIComponent(id)}/tags`);
}

// ── Flow Credentials ──────────────────────────────────────────────────
// Auth-only profiles referenced by flow `action.http`. Distinct from
// api_profiles (which are for HTTP tag-source polling).

export function listCredentials() {
  return request("/api/credentials");
}

export function createCredential(payload) {
  return request("/api/credentials", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCredential(id, payload) {
  return request(`/api/credentials/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteCredential(id) {
  return request(`/api/credentials/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// testCredentialConnection pings the provider's identity endpoint with the
// stored access token. Returns { ok, account_label?, error? } — failures
// surface as ok=false so the UI can render them inline.
export function testCredentialConnection(id) {
  return request(`/api/credentials/${encodeURIComponent(id)}/test`, {
    method: "POST",
  });
}

// OAuth flow — list providers + start a connect.
//
// listOAuthProviders returns [{ id, label, configured, default_scopes }, ...].
// `configured` is false when the deployment hasn't set the provider's
// CS_OAUTH_<NAME>_CLIENT_ID / _CLIENT_SECRET env vars; the UI greys those out.
export function listOAuthProviders() {
  return request("/api/credentials/oauth/providers");
}

// beginOAuthConnect kicks off an OAuth flow. The server returns
// { authorize_url } and the browser is redirected there. The provider then
// redirects the user back to /api/credentials/oauth/{provider}/callback,
// which exchanges the code, persists the credential, and bounces the
// browser to `final_return` (usually the page that initiated the flow).
export function beginOAuthConnect(provider, payload = {}) {
  return request(`/api/credentials/oauth/${encodeURIComponent(provider)}/start`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── OAuth App configuration ──────────────────────────────────────────
// Per-deployment client_id / client_secret per provider. The IT admin
// pastes these from each provider's developer console (api.slack.com/apps,
// console.cloud.google.com, notion.so/my-integrations, ...). Provider
// lookup falls back to env vars when no row exists, so config-as-code
// deployments keep working unchanged.

export function listOAuthApps() {
  return request("/api/oauth-apps");
}

export function getOAuthApp(provider) {
  return request(`/api/oauth-apps/${encodeURIComponent(provider)}`);
}

export function upsertOAuthApp(provider, payload) {
  return request(`/api/oauth-apps/${encodeURIComponent(provider)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteOAuthApp(provider) {
  return request(`/api/oauth-apps/${encodeURIComponent(provider)}`, {
    method: "DELETE",
  });
}

// ── MQTT Brokers + Subscriptions ─────────────────────────────────────
//
// Brokers are managed by core/drivers/mqtt; HTTP layer in core/drivers/
// mqttserver. Plaintext password / TLS-key fields ride inbound on POST
// and PATCH bodies but are never echoed back — the API substitutes
// *_secret_ref strings on read responses.

export function listMqttBrokers() {
  return request("/api/mqtt-brokers");
}

export function createMqttBroker(payload) {
  return request("/api/mqtt-brokers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function provisionHostedMqttBroker() {
  return request("/api/mqtt-brokers/hosted", {
    method: "POST",
  });
}

export function updateMqttBroker(id, patch) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteMqttBroker(id) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getMqttBrokerState(id) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(id)}/state`);
}

// getMqttBrokerDeviceCredentials returns the device connection details for a
// Control Seat-managed (hosted) broker: { public_url, username, password }.
// Returns 404 for non-hosted brokers. The password is what the customer pastes
// into their field devices — display it only on explicit reveal.
export function getMqttBrokerDeviceCredentials(id) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(id)}/device-credentials`);
}

export function updateMqttBrokerDeviceCredentials(id, payload) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(id)}/device-credentials`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function listMqttSubscriptions(brokerId) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(brokerId)}/subscriptions`);
}

export function createMqttSubscription(brokerId, payload) {
  return request(`/api/mqtt-brokers/${encodeURIComponent(brokerId)}/subscriptions`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateMqttSubscription(id, patch) {
  return request(`/api/mqtt-subscriptions/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteMqttSubscription(id) {
  return request(`/api/mqtt-subscriptions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Historian Providers ──────────────────────────────────────────────
//
// Configured historian backends (default_local, TimescaleDB or Tiger Cloud,
// ...). Managed by core/historian/provider; HTTP layer in
// core/historian/provider/server. The plaintext password rides inbound on
// POST/PATCH bodies but is never echoed back — read responses expose only
// a has_password flag.

export function listHistorianProviders() {
  return request("/api/historian-providers");
}

export function createHistorianProvider(payload) {
  return request("/api/historian-providers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateHistorianProvider(id, patch) {
  return request(`/api/historian-providers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteHistorianProvider(id) {
  return request(`/api/historian-providers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// testHistorianProvider pings the provider without opening its live backend.
// Returns { ok, error? } — connection failures surface as ok=false (HTTP 200)
// so the UI can render them inline.
export function testHistorianProvider(id) {
  return request(`/api/historian-providers/${encodeURIComponent(id)}/test`, {
    method: "POST",
  });
}

// ── OPC UA Servers ───────────────────────────────────────────────────
// Managed by core/drivers/opcua; HTTP layer in core/drivers/opcuahttp.
// Password rides inbound on POST/PATCH; never echoed back (HasPassword
// boolean on responses). PATCH uses pointer-style semantics — omit a
// field to leave it unchanged, send "" to clear (Password only).

export function listOpcuaServers() {
  return request("/api/opcua-servers");
}

export function createOpcuaServer(payload) {
  return request("/api/opcua-servers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateOpcuaServer(id, patch) {
  return request(`/api/opcua-servers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteOpcuaServer(id) {
  return request(`/api/opcua-servers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getOpcuaServerState(id) {
  return request(`/api/opcua-servers/${encodeURIComponent(id)}/state`);
}

// Force-rebuild the connection now (new session, monitored items re-attached)
// — the manual counterpart to the backend stuck-offline watchdog. Returns
// the post-rebuild ServerState.
export function reconnectOpcuaServer(id) {
  return request(`/api/opcua-servers/${encodeURIComponent(id)}/reconnect`, {
    method: "POST",
  });
}

// ── Modbus client devices ─────────────────────────────────────────
// These routes manage durable TCP and RTU connections. Per-tag coil/register
// mappings remain part of the unified tag source API and editor.

export function listModbusDevices() {
  return request("/api/modbus/devices");
}

export function getModbusRuntimeStatus() {
  return request("/api/modbus/runtime");
}

export function listModbusSerialPorts() {
  return request("/api/modbus/serial-ports");
}

export async function getPlcCapabilities() {
  return assertPLCCapabilityManifest(await request("/api/plc/capabilities"));
}

export function getPlcFieldbusProbes() {
  return request("/api/plc/fieldbus/probes");
}

// ── Virtual PLC controller inventory ───────────────────────────────
// These endpoints are deliberately configuration-only until the dedicated
// Linux broker/worker capability reports executable runtime evidence.

export function listPLCControllerRuntimes() {
  return request("/api/plc/controllers/runtimes");
}

export function getPLCControllerRuntimeSupport(kind, runtimeId, limit = 20) {
  const bounded = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 20)));
  return request(
    `/api/plc/controllers/runtimes/${encodeURIComponent(kind)}/${encodeURIComponent(runtimeId)}/support?limit=${bounded}`
  );
}

export function listPLCControllers() {
  return request("/api/plc/controllers");
}

export function getPLCControllerOutputDelivery(id, limit = 50, signal) {
  const bounded = Math.max(1, Math.min(200, Math.trunc(Number(limit) || 50)));
  return request(
    `/api/plc/controllers/${encodeURIComponent(id)}/output-delivery?limit=${bounded}`,
    signal ? { signal } : {}
  );
}

export function createPLCController(payload, idempotencyKey) {
  return request("/api/plc/controllers", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(payload),
  });
}

export function updatePLCController(id, payload, idempotencyKey) {
  return request(`/api/plc/controllers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(payload),
  });
}

export function previewPLCControllerDraftChange(id, payload, idempotencyKey) {
  return request(`/api/plc/controllers/${encodeURIComponent(id)}/draft-change/preflight`, {
    method: "POST",
    headers: {
      "Idempotency-Key": idempotencyKey,
      "X-ControlSeat-CSRF": "controller-draft-review-v1",
    },
    body: JSON.stringify(payload),
  });
}

export function confirmPLCControllerDraftChange(id, payload, idempotencyKey) {
  return request(`/api/plc/controllers/${encodeURIComponent(id)}/draft-change/confirm`, {
    method: "POST",
    headers: {
      "Idempotency-Key": idempotencyKey,
      "X-ControlSeat-CSRF": "controller-draft-review-v1",
    },
    body: JSON.stringify(payload),
  });
}

const PLC_CONTROLLER_LIFECYCLE_CSRF = "controller-lifecycle-review-v1";
const PLC_CONTROLLER_LIFECYCLE_ERROR_SCHEMA = "controlseat.plc.controller-lifecycle-error";
const PLC_CONTROLLER_LIFECYCLE_ERROR_MESSAGES = Object.freeze({
  review_csrf_required: "Controller lifecycle review could not be authorized. Refresh the page and try again.",
  permission_denied: "Configure permission is required for controller lifecycle review.",
  authentication_required: "Sign in again before reviewing controller lifecycle actions.",
  invalid_request: "The controller lifecycle request is no longer valid. Refresh the controller and preview it again.",
  not_found: "The controller lifecycle record no longer exists. Refresh the controller list.",
  state_conflict: "The controller state changed or this review expired. Refresh and preview the action again.",
  service_unavailable: "Controller lifecycle review is unavailable on this runtime.",
});

const safePLCControllerLifecycleError = (error) => {
  const statusCode = {
    400: "invalid_request",
    401: "authentication_required",
    403: "permission_denied",
    404: "not_found",
    409: "state_conflict",
    503: "service_unavailable",
  }[Number(error?.status)];
  let code = statusCode || "service_unavailable";
  try {
    const envelope = JSON.parse(String(error?.message || ""));
    if (
      envelope?.schema === PLC_CONTROLLER_LIFECYCLE_ERROR_SCHEMA &&
      envelope?.version === 1 &&
      typeof envelope?.error?.code === "string" &&
      Object.hasOwn(PLC_CONTROLLER_LIFECYCLE_ERROR_MESSAGES, envelope.error.code)
    ) {
      code = envelope.error.code;
    }
  } catch {
    // Network failures and non-contract responses are deliberately collapsed
    // to the same redacted unavailable message.
  }
  const safe = new Error(PLC_CONTROLLER_LIFECYCLE_ERROR_MESSAGES[code]);
  safe.code = code;
  return safe;
};

const requestPLCControllerLifecycle = async (path, payload, idempotencyKey) => {
  try {
    return await request(path, {
      method: "POST",
      headers: {
        "Idempotency-Key": idempotencyKey,
        "X-ControlSeat-CSRF": PLC_CONTROLLER_LIFECYCLE_CSRF,
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw safePLCControllerLifecycleError(error);
  }
};

export function previewPLCControllerLifecycle(id, payload, idempotencyKey) {
  return requestPLCControllerLifecycle(
    `/api/plc/controllers/${encodeURIComponent(id)}/lifecycle/preview`,
    payload,
    idempotencyKey
  );
}

export function confirmPLCControllerLifecycle(id, payload, idempotencyKey) {
  return requestPLCControllerLifecycle(
    `/api/plc/controllers/${encodeURIComponent(id)}/lifecycle/confirm`,
    payload,
    idempotencyKey
  );
}

export function deletePLCController(id, payload, idempotencyKey) {
  return request(`/api/plc/controllers/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(payload),
  });
}

export function listPLCControllerEvents(id, limit = 100) {
  const bounded = Math.max(1, Math.min(500, Math.trunc(Number(limit) || 100)));
  return request(`/api/plc/controllers/${encodeURIComponent(id)}/events?limit=${bounded}`);
}

export function createModbusDevice(payload) {
  return request("/api/modbus/devices", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateModbusDevice(id, patch) {
  return request(`/api/modbus/devices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteModbusDevice(id) {
  return request(`/api/modbus/devices/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getModbusDeviceState(id) {
  return request(`/api/modbus/devices/${encodeURIComponent(id)}/state`);
}

export function reconnectModbusDevice(id) {
  return request(`/api/modbus/devices/${encodeURIComponent(id)}/reconnect`, {
    method: "POST",
  });
}

// EtherNet/IP (Allen-Bradley CIP) client devices. Same outbound-client shape as
// the Modbus device API: per-tag mappings bind through the unified tag source
// endpoint with kind "enip".
export function listEnipDevices() {
  return request("/api/enip/devices");
}

export function getEnipRuntimeStatus() {
  return request("/api/enip/runtime");
}

export function createEnipDevice(payload) {
  return request("/api/enip/devices", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateEnipDevice(id, patch) {
  return request(`/api/enip/devices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteEnipDevice(id) {
  return request(`/api/enip/devices/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getEnipDeviceState(id) {
  return request(`/api/enip/devices/${encodeURIComponent(id)}/state`);
}

export function reconnectEnipDevice(id) {
  return request(`/api/enip/devices/${encodeURIComponent(id)}/reconnect`, {
    method: "POST",
  });
}

// DNP3 (IEEE 1815) master devices. Per-tag mappings bind through the unified
// tag source endpoint with kind "dnp3". The runtime auto-reconnects on its poll
// cadence, so there is no explicit reconnect endpoint.
export function listDnp3Devices() {
  return request("/api/dnp3/devices");
}

export function getDnp3RuntimeStatus() {
  return request("/api/dnp3/runtime");
}

export function createDnp3Device(payload) {
  return request("/api/dnp3/devices", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateDnp3Device(id, patch) {
  return request(`/api/dnp3/devices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteDnp3Device(id) {
  return request(`/api/dnp3/devices/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getDnp3DeviceState(id) {
  return request(`/api/dnp3/devices/${encodeURIComponent(id)}/state`);
}

// Modbus TCP protocol server. This inbound server/slave role is separate
// from the outbound client/master device API above. Configuration contains
// portable tag paths only; internal tag IDs never cross this UI boundary.
export function getModbusServerConfig() {
  return request("/api/modbus/server");
}

export function updateModbusServerConfig(payload) {
  return request("/api/modbus/server", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function getModbusServerStatus() {
  return request("/api/modbus/server/status");
}

export function restartModbusServer() {
  return request("/api/modbus/server/restart", { method: "POST" });
}

// Native OPC UA server role. This is deliberately separate from the outbound
// /api/opcua-servers client/source inventory. The current role is a durable,
// disabled configuration boundary; status truthfully reports why no listener
// can execute yet.
export function getOpcuaServerRoleConfig() {
  return request("/api/opcua/server");
}

// Configure-only projection containing the editable certificate/key/trust
// reference names. Never pass this response into model-visible AI output.
export function getOpcuaServerRoleEditConfig() {
  return request("/api/opcua/server/edit");
}

export function updateOpcuaServerRoleConfig(payload) {
  return request("/api/opcua/server", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function previewOpcuaServerRoleConfig(payload, idempotencyKey) {
  return request("/api/opcua/server/preview", {
    method: "POST",
    headers: {
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      "X-ControlSeat-CSRF": "opcua-server-config-review-v1",
    },
    body: JSON.stringify(payload),
  });
}

export function confirmOpcuaServerRoleConfig(payload, idempotencyKey) {
  return request("/api/opcua/server/confirm", {
    method: "POST",
    headers: {
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      "X-ControlSeat-CSRF": "opcua-server-config-review-v1",
    },
    body: JSON.stringify(payload),
  });
}

export function getOpcuaServerRoleStatus() {
  return request("/api/opcua/server/status");
}

// Bounded classic S7Comm server role. The local/on-prem build may own the
// listener; cloud builds may store disabled configuration only. All public
// mappings use portable tag paths and never return internal tag identities.
export function getS7CommServerConfig() {
  return request("/api/s7comm/server");
}

export function previewS7CommServerConfig(payload) {
  return request("/api/s7comm/server/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateS7CommServerConfig(config, confirmation = {}) {
  return request("/api/s7comm/server", {
    method: "PUT",
    body: JSON.stringify({ config, confirmation }),
  });
}

export function getS7CommServerStatus() {
  return request("/api/s7comm/server/status");
}

export function restartS7CommServer() {
  return request("/api/s7comm/server/restart", { method: "POST" });
}

// Explicit fail-closed recovery for unreadable/corrupt S7Comm durable state.
// The authenticated backend supplies the actor; the GUI supplies a reviewed
// reason and destructive acknowledgement only.
export function repairS7CommServer(reason, acknowledgeDisableAndReplaceState) {
  return request("/api/s7comm/server/repair", {
    method: "POST",
    body: JSON.stringify({
      reason,
      acknowledge_disable_and_replace_state: acknowledgeDisableAndReplaceState === true,
    }),
  });
}

export function getS7CommServerRevisions(limit = 50) {
  const boundedLimit = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 50)));
  return request(`/api/s7comm/server/revisions?limit=${boundedLimit}`);
}

export function getS7CommServerConfigAudit(limit = 50) {
  const boundedLimit = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 50)));
  return request(`/api/s7comm/server/audit?limit=${boundedLimit}`);
}

export function previewS7CommServerRollback(revision) {
  return request(`/api/s7comm/server/revisions/${encodeURIComponent(revision)}/rollback-preview`, {
    method: "POST",
  });
}

export function rollbackS7CommServerRevision(revision, confirmation) {
  return request(`/api/s7comm/server/revisions/${encodeURIComponent(revision)}/rollback`, {
    method: "POST",
    body: JSON.stringify(confirmation),
  });
}

export function opcuaClientCertificateUrl() {
  return `${String(CORE_URL).replace(/\/+$/, "")}/api/opcua/client-certificate`;
}

// ── PI System (AVEVA / OSIsoft) ─────────────────────────────────────
// Managed by core/drivers/pi; HTTP layer in core/drivers/piserver.
// Same shape as OPC UA: Password rides inbound on POST/PATCH and is
// never echoed back (HasPassword boolean on responses). PATCH uses
// pointer-style semantics — omit a field to leave it unchanged, send
// "" to clear (Password only).
export function listPiServers() {
  return request("/api/pi/servers");
}

export function createPiServer(payload) {
  return request("/api/pi/servers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updatePiServer(id, patch) {
  return request(`/api/pi/servers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deletePiServer(id) {
  return request(`/api/pi/servers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getPiServerState(id) {
  return request(`/api/pi/servers/${encodeURIComponent(id)}/state`);
}

export function testPiServer(id) {
  return request(`/api/pi/servers/${encodeURIComponent(id)}/test`, {
    method: "POST",
  });
}

// ── Auth ─────────────────────────────────────────────────────────────

export function getAuthStatus() {
  return request("/auth/status");
}

export function getMe() {
  return request("/auth/me");
}

export function updateProfile(fields) {
  return request("/auth/me", {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
}

export function updatePreferences(patch) {
  return request("/auth/preferences", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function changePassword(payload) {
  return request("/auth/change-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── Projects ─────────────────────────────────────────────────────────

function publishRequest(path, options = {}) {
  return fetch(`${PUBLISH_URL}${path}`, {
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
    credentials: "include",
  }).then((res) => {
    if (!res.ok) return res.text().then((t) => { throw new Error(t || `${res.status} ${res.statusText}`); });
    if (res.status === 204) return null;
    return res.json();
  });
}

export function listProjects() {
  return publishRequest("/api/projects");
}

export function createProject(payload) {
  return publishRequest("/api/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateProject(id, payload) {
  return publishRequest(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function listProjectPages(projectId) {
  return publishRequest(`/api/pages?projectId=${encodeURIComponent(projectId)}`);
}

export function getScreen(id) {
  return publishRequest(`/api/screens/${encodeURIComponent(id)}`);
}

export function uploadAvatar(dataUrl, userId, existingSrc) {
  return publishRequest("/api/avatar", {
    method: "POST",
    body: JSON.stringify({ dataUrl, userId, existingSrc }),
  });
}

export function saveScreen(payload) {
  return publishRequest("/api/screens/save", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteProject(id) {
  return publishRequest(`/api/projects/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Workspace Settings ──────────────────────────────────────────────

export function getWorkspaceSettings() {
  return publishRequest("/api/workspace-settings");
}

export function updateWorkspaceSettings(payload) {
  return publishRequest("/api/workspace-settings", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// ── PLC Edge Runtimes ───────────────────────────────────────────────

export function listEdgeGateways() {
  return request("/api/edge/gateways");
}

export function listEdgeControlPlaneCandidates() {
  return request("/api/edge/control-plane-candidates");
}

export function listEdgeHardwareProfiles() {
  return request("/api/edge/hardware-profiles");
}

export function getEdgeRuntimeHardware(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware`);
}

export function getEdgeRuntimePackageReadiness(hardwareProfile) {
  const query = new URLSearchParams({ hardware_profile: String(hardwareProfile || "").trim() });
  return request(`/api/edge/runtime-package-readiness?${query}`);
}

export function createEdgeGateway(payload) {
  return request("/api/edge/gateways", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateEdgeGatewayEnrollmentSettings(id, payload) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/enrollment-settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function rotateEdgeGatewayToken(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/rotate-token`, {
    method: "POST",
  });
}

export function updateEdgeGatewayRuntime(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/update-runtime`, {
    method: "POST",
  });
}

export function rollbackEdgeGatewayRuntime(id, confirmation) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/rollback-runtime`, {
    method: "POST",
    body: JSON.stringify(confirmation),
  });
}

export function updateEdgeGatewayRuntimeTrust(id, confirmation) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/update-runtime-trust`, {
    method: "POST",
    body: JSON.stringify(confirmation),
  });
}

export function runEdgeFR202SelfTest(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/self-test`, {
    method: "POST",
  });
}

export function readEdgeFR202ChannelSample(id, terminal) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/channel-sample`, {
    method: "POST",
    body: JSON.stringify({ terminal }),
  });
}

export function getEdgeFR202RetirementStatus(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/retirement`);
}

export function removeEdgeFR202HardwareProfile(id, confirmation) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/remove-profile`, {
    method: "POST",
    body: JSON.stringify(confirmation),
  });
}

export function retireEdgeFR202Tags(id, confirmation) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/retire-tags`, {
    method: "POST",
    body: JSON.stringify(confirmation),
  });
}

export function deleteEdgeGateway(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function listEdgeGatewayCommands(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/commands`);
}

export function listEdgePLCPrograms(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/plc/programs`);
}

export function listPLCProgramsForFleet() {
  return request("/api/plc/programs");
}

export function getPLCDeploymentSigningPolicy() {
  return request("/api/plc/deployment-signing-policy");
}

export function previewPLCDeploymentSigningPolicy(payload) {
  return request("/api/plc/deployment-signing-policy/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function confirmPLCDeploymentSigningPolicy(payload) {
  return request("/api/plc/deployment-signing-policy/confirm", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listPLCFleetRollouts() {
  return request("/api/edge/plc-fleet-rollouts");
}

export function previewPLCFleetRollout(payload) {
  return request("/api/edge/plc-fleet-rollouts/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getPLCFleetRollout(id) {
  return request(`/api/edge/plc-fleet-rollouts/${encodeURIComponent(id)}`);
}

export function listPLCFleetRolloutEvents(id) {
  return request(`/api/edge/plc-fleet-rollouts/${encodeURIComponent(id)}/events`);
}

export function confirmPLCFleetRollout(id, planSHA256, reason) {
  return request(`/api/edge/plc-fleet-rollouts/${encodeURIComponent(id)}/confirm`, {
    method: "POST",
    body: JSON.stringify({ plan_sha256: planSHA256, reason }),
  });
}

export function rejectPLCFleetRolloutDraft(id, planSHA256, reason) {
  return request(`/api/edge/plc-fleet-rollouts/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify({ plan_sha256: planSHA256, reason }),
  });
}

export function controlPLCFleetRollout(id, action, payload = {}) {
  return request(
    `/api/edge/plc-fleet-rollouts/${encodeURIComponent(id)}/${encodeURIComponent(action)}`,
    { method: "POST", body: action === "resume" ? "{}" : JSON.stringify(payload) }
  );
}

export function setEdgePLCProgramEnabled(id, programID, enabled) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/plc/enabled`, {
    method: "POST",
    body: JSON.stringify({ program_id: programID, enabled: Boolean(enabled) }),
  });
}

export function listEdgeGPIOBindings(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/gpio-bindings`);
}

export function listEdgeConnectors(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/connectors`);
}

export function restoreFR202TerminalTags(id) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/hardware/fr202/restore-tags`, {
    method: "POST",
  });
}

export function getEdgeTagCatalog(id, options = {}) {
  const params = new URLSearchParams();
  if (String(options.query || "").trim()) params.set("q", String(options.query).trim());
  if (Number.isInteger(options.offset) && options.offset >= 0) params.set("offset", String(options.offset));
  if (Number.isInteger(options.limit) && options.limit > 0) params.set("limit", String(options.limit));
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/tag-catalog${suffix}`);
}

export function inspectEdgeTagCatalog(id, paths) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/tag-catalog/inspect`, {
    method: "POST",
    body: JSON.stringify({ paths }),
  });
}

// Create one ordinary manual tag in the cloud project (root folder). Used by
// the I/O connection flow's inline "new tag": the same declaration is then
// deployed to the runtime with deployEdgeTags so both sides agree on
// path/type/writability.
export function createOrdinaryTag({ name, path, type, writable, description }) {
  return request("/api/tags", {
    method: "POST",
    body: JSON.stringify({
      name,
      path,
      type,
      writable: Boolean(writable),
      ...(description ? { description } : {}),
      historian_enabled: true,
    }),
  });
}

// Declare ordinary tags to the runtime's local store (tags.deploy.v1). This
// creates tag definitions on the device only — no values, no I/O binding.
export function deployEdgeTags(id, tags) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/tags/deploy`, {
    method: "POST",
    body: JSON.stringify({ tags }),
  });
}

export function previewEdgeConnector(id, spec, expectedRevision) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/connectors/preview`, {
    method: "POST",
    body: JSON.stringify({
      spec,
      ...(Number.isSafeInteger(expectedRevision) && expectedRevision >= 0
        ? { expected_revision: expectedRevision }
        : {}),
    }),
  });
}

export function confirmEdgeConnector(id, preview, confirmation = {}) {
  return request(`/api/edge/gateways/${encodeURIComponent(id)}/connectors/confirm`, {
    method: "POST",
    body: JSON.stringify({
      connector_id: preview.connector_id,
      revision: preview.revision,
      sha256: preview.sha256,
      ...(Number.isSafeInteger(preview.expected_revision)
        ? { expected_revision: preview.expected_revision }
        : {}),
      ...(confirmation.acknowledge_no_application_authentication === true
        ? { acknowledge_no_application_authentication: true }
        : {}),
    }),
  });
}

export function listEdgeConnectorHistory(id, connectorID) {
  return request(
    `/api/edge/gateways/${encodeURIComponent(id)}/connectors/${encodeURIComponent(connectorID)}/history`
  );
}

export function retryEdgeConnector(id, connectorID) {
  return request(
    `/api/edge/gateways/${encodeURIComponent(id)}/connectors/${encodeURIComponent(connectorID)}/retry`,
    { method: "POST" }
  );
}

// Remove a connection. The backend queues a signed stop to the device first
// when the connection is running, then deletes it from the list.
export function deleteEdgeConnector(id, connectorID, expectedRevision) {
  const revision = Number(expectedRevision);
  if (!Number.isSafeInteger(revision) || revision < 1) {
    throw new Error("Refresh the connection and try again.");
  }
  const query = new URLSearchParams({ expected_revision: String(revision) });
  return request(
    `/api/edge/gateways/${encodeURIComponent(id)}/connectors/${encodeURIComponent(connectorID)}?${query}`,
    { method: "DELETE" }
  );
}

export function previewEdgeConnectorLifecycle(id, connectorID, desiredState) {
  return request(
    `/api/edge/gateways/${encodeURIComponent(id)}/connectors/${encodeURIComponent(connectorID)}/lifecycle-preview`,
    { method: "POST", body: JSON.stringify({ desired_state: desiredState }) }
  );
}

export function previewEdgeConnectorRollback(id, connectorID, revision) {
  return request(
    `/api/edge/gateways/${encodeURIComponent(id)}/connectors/${encodeURIComponent(connectorID)}/revisions/${encodeURIComponent(revision)}/rollback-preview`,
    { method: "POST" }
  );
}

// ── Users ────────────────────────────────────────────────────────────

// ── Groups ───────────────────────────────────────────────────────────

export function listGroups() {
  return request("/auth/groups");
}

export function createGroup(payload) {
  return request("/auth/groups", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getGroup(id) {
  return request(`/auth/groups/${encodeURIComponent(id)}`);
}

export function updateGroup(id, payload) {
  return request(`/auth/groups/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteGroup(id) {
  return request(`/auth/groups/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function addGroupMembers(id, userIds) {
  return request(`/auth/groups/${encodeURIComponent(id)}/members`, {
    method: "POST",
    body: JSON.stringify({ user_ids: userIds }),
  });
}

export function removeGroupMembers(id, userIds) {
  return request(`/auth/groups/${encodeURIComponent(id)}/members`, {
    method: "DELETE",
    body: JSON.stringify({ user_ids: userIds }),
  });
}

// ── Users ────────────────────────────────────────────────────────────

export function listUsers() {
  return request("/auth/users");
}

export function createUser(payload) {
  return request("/auth/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateUserRole(id, role) {
  return request(`/auth/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export function updateUser(id, fields) {
  return request(`/auth/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
}

export function deleteUser(id) {
  return request(`/auth/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Invites ─────────────────────────────────────────────────────────

export function createInvite(payload) {
  return request("/auth/invites", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listInvites() {
  return request("/auth/invites");
}

export function revokeInvite(id) {
  return request(`/auth/invites/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── AI Providers ─────────────────────────────────────────────────────

export function listAiProviders() {
  return request("/api/ai/providers");
}

export function createAiProvider(payload) {
  return request("/api/ai/providers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAiProvider(id, payload) {
  return request(`/api/ai/providers/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteAiProvider(id) {
  return request(`/api/ai/providers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function setDefaultAiProvider(id) {
  return request(`/api/ai/providers/${encodeURIComponent(id)}/default`, {
    method: "PUT",
  });
}

// ── AI Preference (per-user) ─────────────────────────────────────────

export function getAiPreference() {
  return request("/api/ai/preference");
}

export function saveAiPreference(payload) {
  return request("/api/ai/preference", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteAiPreference() {
  return request("/api/ai/preference", { method: "DELETE" });
}

export function getAiUsage() {
  return request("/api/ai/usage");
}

// ── Team memory ─────────────────────────────────────────────────────
//
// The plant's shared durable facts, one row each, with a review state. The
// agent proposes; a human approves, edits, rejects or supersedes. Only
// active facts reach anyone's prompt, so the approval queue below is the
// only thing standing between a model's guess and every teammate's context.

export function listTeamMemory({ status, type, q, limit } = {}) {
  const params = new URLSearchParams();
  // status is sent even when empty: an empty value means "every status",
  // while omitting it entirely means "active", which is a different query.
  if (status !== undefined) params.set("status", status);
  if (type) params.set("type", type);
  if (q) params.set("q", q);
  if (limit) params.set("limit", String(limit));
  const query = params.toString();
  return request(`/api/ai/memory/team${query ? `?${query}` : ""}`);
}

export function createTeamMemoryFact(payload) {
  return request("/api/ai/memory/team/facts", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function approveTeamMemoryFact(id) {
  return request(`/api/ai/memory/team/facts/${encodeURIComponent(id)}/approve`, {
    method: "POST",
  });
}

export function rejectTeamMemoryFact(id) {
  return request(`/api/ai/memory/team/facts/${encodeURIComponent(id)}/reject`, {
    method: "POST",
  });
}

export function updateTeamMemoryFact(id, payload) {
  return request(`/api/ai/memory/team/facts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function supersedeTeamMemoryFact(id, payload) {
  return request(`/api/ai/memory/team/facts/${encodeURIComponent(id)}/supersede`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteTeamMemoryFact(id) {
  return request(`/api/ai/memory/team/facts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// The export is text/markdown, not JSON, so it cannot go through request().
// Returned as a string for the caller to hand to a download or a clipboard
// copy — the page does both.
export async function exportTeamMemoryMarkdown() {
  const res = await fetch(`${CORE_URL}/api/ai/memory/team/export.md`, {
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.text();
}

// ── AI Assistant history ────────────────────────────────────────────
//
// The durable log of assistant conversations behind the Assistant History
// page. Not a per-user store: every operator reads every conversation, which
// is the point of an investigation log.

export function listAssistantConversations({ limit, cursor, q } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  if (q) params.set("q", q);
  const query = params.toString();
  return request(`/api/ai/assistant/conversations${query ? `?${query}` : ""}`);
}

export function getAssistantConversation(id) {
  return request(`/api/ai/assistant/conversations/${encodeURIComponent(id)}`);
}

// saveAssistantConversation upserts the whole conversation under a
// client-generated id. It is called once per turn and is idempotent, so a save
// that never lands is repaired by the next one — see assistantHistory.js.
export function saveAssistantConversation(id, payload) {
  return request(`/api/ai/assistant/conversations/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ── Agent conversations (resumable) ─────────────────────────────────
//
// The store behind session continuity: the thread the drawer picks up again
// after F5, a browser restart, or a jump into the editor. Deliberately NOT the
// same thing as the assistant history above — that one is an append-only
// investigation log nobody owns, this one is an owned thread appended to a
// message at a time. Both are written on every turn; see
// ai-integration/agentConversation.js.

export function listAgentConversations({ limit, cursor, q, scope, surface, archived } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  if (q) params.set("q", q);
  if (scope) params.set("scope", scope);
  if (surface) params.set("surface", surface);
  if (archived) params.set("archived", "true");
  const query = params.toString();
  return request(`/api/agent/conversations${query ? `?${query}` : ""}`);
}

// getAgentConversation returns the header plus the whole transcript, which is
// what a resume needs. Pass afterSeq to fetch only what this tab is missing.
export function getAgentConversation(id, { afterSeq } = {}) {
  const params = new URLSearchParams();
  if (Number.isFinite(afterSeq)) params.set("afterSeq", String(afterSeq));
  const query = params.toString();
  return request(
    `/api/agent/conversations/${encodeURIComponent(id)}${query ? `?${query}` : ""}`
  );
}

// createAgentConversation is idempotent under the client-generated id: a
// create that never landed is repaired by the next one instead of forking a
// second thread.
export function createAgentConversation(payload) {
  return request("/api/agent/conversations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// appendAgentMessage writes one message at an explicit seq. A 409 means
// another tab took that position; the error carries `body.nextSeq` and the
// caller resends the same message there. See appendWithRetry in
// ai-integration/agentConversation.js.
export function appendAgentMessage(id, payload) {
  return request(`/api/agent/conversations/${encodeURIComponent(id)}/messages`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchAgentConversation(id, payload) {
  return request(`/api/agent/conversations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// ── Agent skills (BETA-31) ──────────────────────────────────────────
// A skill is a named, versioned recipe of tool calls — see
// core/intelligence/agentskills and AGENT-DESIGN.md §2.3.

export function listAgentSkills() {
  return request("/api/agent/skills");
}

export function getAgentSkill(name, { version } = {}) {
  const query = Number.isFinite(version) ? `?version=${version}` : "";
  return request(`/api/agent/skills/${encodeURIComponent(name)}${query}`);
}

export function listAgentSkillVersions(name) {
  return request(`/api/agent/skills/${encodeURIComponent(name)}/versions`);
}

export function createAgentSkill(payload) {
  return request("/api/agent/skills", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// newAgentSkillVersion appends version current+1; it never overwrites an
// existing version.
export function newAgentSkillVersion(name, payload) {
  return request(`/api/agent/skills/${encodeURIComponent(name)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ── Alarm Users ─────────────────────────────────────────────────────

// listTags lists tags visible at the global scope. Worksheet-scoped
// formula tags are excluded by default; pass `worksheetId` to also
// include formulas owned by that worksheet (the Trends Workbench
// passes its current id so its formulas appear in the local Add Tag
// picker without leaking into others).
export function listTags({ worksheetId } = {}) {
  const params = worksheetId ? `?include_worksheet=${encodeURIComponent(worksheetId)}` : "";
  return request(`/api/tags${params}`);
}

// getFolderTree returns the tag tree. Same worksheet-scoping rules
// as listTags — pass `worksheetId` to include that worksheet's
// formulas in the result.
export function getFolderTree({ worksheetId, lite = false, countsOnly = false } = {}) {
  const params = new URLSearchParams();
  if (worksheetId) params.set("include_worksheet", worksheetId);
  if (lite) params.set("metadata", "lite");
  if (countsOnly) params.set("counts_only", "1");
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return request(`/api/tag-folders/tree${suffix}`);
}

export function browseTagFolder(folderId, { worksheetId, limit = 250, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (worksheetId) params.set("include_worksheet", worksheetId);
  return request(`/api/tag-folders/${encodeURIComponent(folderId || "__root__")}/browse?${params.toString()}`);
}

export function searchTags({ query, worksheetId, limit = 250, offset = 0 } = {}) {
  const params = new URLSearchParams({
    q: String(query || "").trim(),
    limit: String(limit),
    offset: String(offset),
  });
  if (worksheetId) params.set("include_worksheet", worksheetId);
  return request(`/api/tags/search?${params.toString()}`);
}

export function listAlarmUsers() {
  return request("/api/alarms/users");
}

export function listAlarms() {
  return request("/api/alarms");
}

export function createAlarm(tagId, payload) {
  return request(`/api/tags/${encodeURIComponent(tagId)}/alarms`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAlarm(id, payload) {
  return request(`/api/alarms/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteAlarm(id) {
  return request(`/api/alarms/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function acknowledgeAlarm(id) {
  return request(`/api/alarms/${encodeURIComponent(id)}/acknowledge`, {
    method: "POST",
  });
}

// ── License / TLS / Updates / System Info ───────────────────────────

// fetchTLSConfig returns the current TLS config from driverhost.
// Returns { configured, config?, https_listen } or null on 404 (cloud).
export async function fetchTLSConfig() {
  const res = await fetch(`${CORE_URL}/api/tls-config`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// uploadTLSConfig posts a cert + key pair as multipart. Returns the
// updated tlsResponse on success; throws on bad cert/key + 4xx errors.
export async function uploadTLSConfig(certFile, keyFile) {
  const form = new FormData();
  form.append("cert", certFile);
  form.append("key", keyFile);
  const res = await fetch(`${CORE_URL}/admin/tls-config`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text.trim() || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// checkForUpdates calls /api/updates/check on driverhost (admin only).
// Returns { current, latest, update_needed, reachable, ... } so the
// Updates page can render the right state.
export async function checkForUpdates() {
  return request("/api/updates/check");
}

// fetchSystemInfo returns the public system info (version, license mode,
// etc.). Used by the Updates page to render the current version on
// initial load — no phone-home required.
export async function fetchSystemInfo() {
  const res = await fetch(`${CORE_URL}/api/system-info`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// fetchLicense returns the license info payload, or null when the
// backend reports no licensing in this deployment (cloud SaaS — 404).
export async function fetchLicense() {
  const res = await fetch(`${CORE_URL}/api/license`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// uploadLicense posts a license.json file as multipart and returns the
// refreshed license info payload. The server verifies the signature
// before writing to disk; a tampered upload returns 400.
export async function uploadLicense(file) {
  const form = new FormData();
  form.append("license", file);
  const res = await fetch(`${CORE_URL}/admin/license`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text.trim() || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// removeLicense deletes the on-disk license file and swaps the running
// gateway to an unlicensed state. Used by the License page's "Remove
// license" button. Returns the post-removal license info payload (mode
// will be "unlicensed").
export async function removeLicense() {
  const res = await fetch(`${CORE_URL}/admin/license`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text.trim() || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// ─── Historian helpers reused by the Trends Workbench ──────────────────
//
// platform.js already calls these inline; pulled out here so the Trends
// section + other future analytics pages can share a single client.

export function resolveTagPathToId(tagPath) {
  return request(`/api/tags/by-full-path?path=${encodeURIComponent(tagPath)}`)
    .then((res) => res?.id ?? null);
}

// Set one tag's historian policy (PUT /api/tags/{id}/historian). Body fields:
// enabled, historian_mode (0 on_change, 1 interval, 2 both),
// historian_interval_ms (>=500 when mode 1/2), deadband_mode, deadband_value.
export function setTagHistorianPolicy(tagID, policy) {
  return request(`/api/tags/${encodeURIComponent(tagID)}/historian`, {
    method: "PUT",
    body: JSON.stringify(policy),
  });
}

// fetchTagById returns the BrowserTag shape (includes fullPath + name)
// for a given numeric tag id. The Trends Workbench uses this to resolve
// user-visible labels for tags hydrated from URL params or worksheets
// where only the id is known — IDs MUST NEVER reach the UI directly.
export function fetchTagById(tagId) {
  return request(`/api/tags/${encodeURIComponent(tagId)}/browser`);
}

// Resolve only the tags a page is already displaying. This avoids loading the
// complete folder tree merely to turn a handful of stored tag IDs into paths
// on large installations. Requests are deliberately bounded so an alarm or
// profile page cannot create an unbounded burst after reconnecting.
export async function fetchTagsByIds(tagIds, { concurrency = 8 } = {}) {
  const ids = Array.from(new Set((tagIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean)));
  const width = Math.max(1, Math.min(16, Number(concurrency) || 8));
  const tags = [];
  for (let start = 0; start < ids.length; start += width) {
    const page = await Promise.all(ids.slice(start, start + width).map((id) => (
      fetchTagById(id).catch(() => null)
    )));
    page.forEach((tag) => { if (tag) tags.push(tag); });
  }
  return tags;
}

// The historian analytics endpoints all encode their response as a bare
// JSON array via writeJSON (see core/historian/server/api_analytics.go),
// not as a wrapper object. Returning res directly — falling back to []
// only when the server hands back a non-array (e.g. an error envelope).
function asArray(res) {
  return Array.isArray(res) ? res : [];
}

export function fetchHistoryAggregate({ tagId, start, end, interval }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
  });
  if (interval) params.set("interval", interval);
  return request(`/api/history/aggregate?${params.toString()}`).then(asArray);
}

// fetchHistoryLast returns the N most-recent samples for a tag regardless
// of time window. Used by the trends chart as a carry-forward anchor so
// slow-changing tags (e.g. a utility rate that only ticks hourly) still
// render a line in zoomed windows that contain no sample changes.
export function fetchHistoryLast({ tagId, n = 1 }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    n: String(n),
  });
  return request(`/api/history/last?${params.toString()}`).then(asArray);
}

// fetchHistoryRange returns RAW samples for a tag within [start, end] —
// each row is a single historian sample with its precise timestamp,
// NOT a bucketed aggregate. Used by the trends chart at narrow zoom
// levels so step changes plot at the exact sample timestamp (matching
// where the anomaly detector says the change occurred), instead of at
// the bucket center which can be off by up to half an interval.
export function fetchHistoryRange({ tagId, start, end, limit = 5000, order = "asc" }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
    limit: String(limit),
    order,
  });
  return request(`/api/history?${params.toString()}`).then(asArray);
}

// fetchHistoryAnomalies wraps /api/intelligence/analysis/anomalies with the bench-tuned
// defaults — method=auto + cluster_gap_seconds=60 = the same combination
// that scored F1=0.44 on the SKAB+synthetic corpus. The Trends Workbench
// uses each row's event_start/event_end metadata for duration, and keeps
// the representative timestamp for the visible chart marker.
export function fetchHistoryAnomalies({ tagId, start, end, method = "auto", clusterGapSeconds = 60, threshold, percentile, window }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
    method,
    cluster_gap_seconds: String(clusterGapSeconds),
  });
  if (threshold != null) params.set("threshold", String(threshold));
  if (percentile != null) params.set("percentile", String(percentile));
  if (window != null) params.set("window", String(window));
  return request(`/api/intelligence/analysis/anomalies?${params.toString()}`).then(asArray);
}

export function fetchTimeseriesStats({ tagId, start, end }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
  });
  return request(`/api/intelligence/analysis/analyze?${params.toString()}`);
}

export function fetchDistribution({ tagId, start, end, bins, percentiles, approximate }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
  });
  if (Number.isFinite(Number(bins))) params.set("bins", String(bins));
  if (percentiles) params.set("percentiles", String(percentiles));
  if (approximate) params.set("approximate", "true");
  return request(`/api/intelligence/analysis/distribution?${params.toString()}`);
}

export function fetchRateOfChange({ tagId, start, end, unit }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
  });
  if (unit) params.set("unit", unit);
  return request(`/api/intelligence/analysis/rate-of-change?${params.toString()}`);
}

// --- Computed tags (formula bar) -------------------------------------------

// createComputedTag creates a new tag whose value is derived from other
// tags via the server-side compute engine. Caller supplies path,
// folder, and an expression (e.g. "$default/pump1/kw / $default/pump1/flow_gpm").
// The expression's tag references are also persisted in the
// computed-source's `source_paths` array so the editor's existing
// computed-source UI continues to make sense for these tags.
export function createComputedTag({ name, folderId, expression, sourcePaths, worksheetId }) {
  if (!folderId) return Promise.reject(new Error("folderId is required"));
  // Always namespace formula paths so they never collide with each
  // other or with global tags. The user-visible `name` stays as typed;
  // only the stored `path` carries the prefix. Pickers + lookups
  // resolve via fullPath / name, so this is invisible to the UI.
  //
  // Three layers of namespacing:
  //   - "_ws_" prefix marks this as a worksheet-scoped formula
  //   - worksheet id (or "draft" for unsaved worksheets) groups
  //     formulas that belong together
  //   - random suffix guarantees uniqueness even within a single
  //     worksheet (so creating "efficiency" and then deleting+
  //     recreating it doesn't have to wait for cascade cleanup)
  const wsToken = worksheetId ? String(worksheetId).slice(0, 12) : "draft";
  const rand = Math.random().toString(36).slice(2, 8);
  // Collapse non-ASCII / empty names to a stable token. Without the
  // fallback, a Unicode-only name like "温度" becomes "" and the path
  // ends with a triple underscore; "f" keeps it visually clean.
  const safeName = (String(name || "").replace(/[^A-Za-z0-9_]+/g, "_").slice(0, 32) || "f");
  const path = `_ws_${wsToken}__${safeName}__${rand}`;
  return request("/api/tags", {
    method: "POST",
    body: JSON.stringify({
      name,
      path,
      type: "float64",
      writable: false,
      folder_id: folderId,
      description: `Computed: ${expression}`,
      source: {
        kind: "computed",
        config: {
          source_paths: sourcePaths || [],
          script: expression,
          // worksheet_id marks this tag as worksheet-scoped (the
          // "Formula" feature). Frontend pickers + AI tools filter
          // these out except inside the owning worksheet.
          ...(worksheetId ? { worksheet_id: worksheetId } : {}),
        },
      },
      historian_enabled: true,
    }),
  });
}

// updateComputedTag patches an existing formula's name + expression.
// Path stays as-is (renaming a formula doesn't reshuffle its random
// suffix). source_paths is rewritten from the new expression's refs
// so a dropped reference doesn't leak permissions / keep the formula
// listening to a tag it no longer needs.
export function updateComputedTag({ tagId, name, expression, sourcePaths, worksheetId }) {
  if (!tagId) return Promise.reject(new Error("tagId is required"));
  return request(`/api/tags/${encodeURIComponent(tagId)}`, {
    method: "PUT",
    body: JSON.stringify({
      name,
      description: `Computed: ${expression}`,
      source: {
        kind: "computed",
        config: {
          source_paths: sourcePaths || [],
          script: expression,
          ...(worksheetId ? { worksheet_id: worksheetId } : {}),
        },
      },
    }),
  });
}

// isWorksheetScopedFormula returns the worksheet id this tag is
// scoped to, or empty when the tag is unscoped (a global tag the
// user can see anywhere). Centralized here so every picker, the AI
// tools, and the workbench filter use the same definition.
export function worksheetScopedFor(tag) {
  if (!tag) return "";
  const kind = tag.source_kind || tag.source?.kind;
  if (kind !== "computed") return "";
  const cfg = tag.source?.config;
  if (!cfg) return "";
  if (typeof cfg === "string") {
    try { return JSON.parse(cfg).worksheet_id || ""; } catch { return ""; }
  }
  return cfg.worksheet_id || "";
}

// --- Conditions (Trends predicate-driven capsules) -------------------------

export function listConditions() {
  return request("/api/conditions").then((res) => res?.conditions || []);
}

export function createCondition(payload) {
  return request("/api/conditions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCondition(id, patch) {
  return request(`/api/conditions/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteCondition(id) {
  return request(`/api/conditions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function fetchConditionCapsules({ id, start, end }) {
  const params = new URLSearchParams({ start, end });
  return request(`/api/conditions/${encodeURIComponent(id)}/capsules?${params}`)
    .then((res) => res?.capsules || []);
}

// fetchConditionStats returns { count, total_duration_ms, first_start,
// last_end } for capsules in the window. Drives the per-condition
// summary badge in the sidebar.
export function fetchConditionStats({ id, start, end }) {
  const params = new URLSearchParams({ start, end });
  return request(`/api/conditions/${encodeURIComponent(id)}/stats?${params}`);
}

// fetchConditionAggregate returns { count, mean, min, max, sum } for a
// tag's values restricted to the condition's capsules. Underlies the
// Statistics panel's "during $condition" toggle.
export function fetchConditionAggregate({ id, tagId, start, end }) {
  const params = new URLSearchParams({ tag_id: String(tagId), start, end });
  return request(`/api/conditions/${encodeURIComponent(id)}/aggregate?${params}`);
}

export function fetchChangePoints({ tagId, start, end, threshold, window }) {
  const params = new URLSearchParams({
    tag_id: String(tagId),
    start,
    end,
  });
  if (threshold != null) params.set("threshold", String(threshold));
  if (window != null) params.set("window", String(window));
  return request(`/api/intelligence/analysis/changepoints?${params.toString()}`).then(asArray);
}

// ── Ignition Gateways ──────────────────────────────────────────────────
// A gateway is registered here (name + API key); the Ignition module dials
// CORE_URL's /api/ignition/agent with that key, announces its tag tree, and
// streams values. PATCH omits a field to leave it unchanged — rename sends
// only { name }, rotate-key sends only { api_key } (an empty api_key clears
// the key). The plaintext key is never returned, so it is shown once at
// create/rotate; recovery is a key rotation.

export function listIgnitionGateways() {
  return request("/api/ignition/gateways").then(asArray);
}

export function createIgnitionGateway(payload) {
  return request("/api/ignition/gateways", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateIgnitionGateway(id, patch) {
  return request(`/api/ignition/gateways/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteIgnitionGateway(id) {
  return request(`/api/ignition/gateways/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getIgnitionGatewayState(id) {
  return request(`/api/ignition/gateways/${encodeURIComponent(id)}/state`);
}

export function getIgnitionGatewayTagCount(id) {
  return request(`/api/ignition/gateways/${encodeURIComponent(id)}/tag-count`).then(
    (r) => r?.count ?? 0
  );
}

// ignitionAgentUrl is the WebSocket endpoint the module dials. It targets
// CORE_URL (the backend the module connects to, not the browser origin),
// http→ws, with the agent path appended.
export function ignitionAgentUrl() {
  const ws = String(CORE_URL).replace(/^http/, "ws").replace(/\/+$/, "");
  return `${ws}/api/ignition/agent`;
}

// generateIgnitionApiKey returns a 32-byte hex string from the Web Crypto
// CSPRNG. The module sends it verbatim as X-CS-Key; the backend stores only a
// reference, so this value is shown to the operator exactly once.
export function generateIgnitionApiKey() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ── VTScada Connections ────────────────────────────────────────────────

export function listVTScadaConnections() {
  return request("/api/vtscada/connections").then(asArray);
}

export function createVTScadaConnection(payload) {
  return request("/api/vtscada/connections", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateVTScadaConnection(id, patch) {
  return request(`/api/vtscada/connections/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteVTScadaConnection(id) {
  return request(`/api/vtscada/connections/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function testVTScadaConnection(id) {
  return request(`/api/vtscada/connections/${encodeURIComponent(id)}/test`, {
    method: "POST",
  });
}

// ── Document Sources ──────────────────────────────────────────────────

export function listFileSources() {
  return request("/api/file-sources").then(asArray);
}

export function createFileSource(payload) {
  return request("/api/file-sources", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateFileSource(id, patch) {
  return request(`/api/file-sources/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteFileSource(id) {
  return request(`/api/file-sources/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// Answers 200 with {ok, error} whenever the probe actually ran, so a rejected
// credential is data to display rather than a thrown request.
export function testFileSource(id) {
  return request(`/api/file-sources/${encodeURIComponent(id)}/test`, {
    method: "POST",
  });
}

export function syncFileSource(id) {
  return request(`/api/file-sources/${encodeURIComponent(id)}/sync`, {
    method: "POST",
  });
}

// Turns a pasted site URL into pickable libraries. Send connection_id to reuse a
// saved connection's stored secret instead of asking for it again.
export function resolveFileSourceLibraries(payload) {
  return request("/api/file-sources/resolve-libraries", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── Custom connectors (collectors) ──────────────────────────────────
//
// Operator-authored Python scripts the driverhost runs on a schedule; their
// named outputs land in ordinary tags. See docs/battex/10-custom-collectors.md
// for the stdin/stdout contract these endpoints are built around.

export function listCollectors() {
  return request("/api/collectors");
}

export function getCollector(id) {
  return request(`/api/collectors/${encodeURIComponent(id)}`);
}

export function createCollector(payload) {
  return request("/api/collectors", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Every field of the PUT body is optional and an omitted one is left alone, so
// a caller that edits only the script cannot clobber a concurrent edit of the
// outputs.
export function updateCollector(id, patch) {
  return request(`/api/collectors/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteCollector(id) {
  return request(`/api/collectors/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function setCollectorEnabled(id, enabled) {
  return request(
    `/api/collectors/${encodeURIComponent(id)}/${enabled ? "enable" : "disable"}`,
    { method: "POST" }
  );
}

// Answers 200 with {values, stdout, stderr, duration_ms, error} whenever the
// script actually ran, so a crashed script is data to display rather than a
// thrown request. A dry run converts every value but writes no tags and
// records no run.
export function runCollectorNow(id, { dryRun = false } = {}) {
  const query = dryRun ? "?dry_run=1" : "";
  return request(`/api/collectors/${encodeURIComponent(id)}/run-now${query}`, {
    method: "POST",
  });
}

// The cheap polling shape: status without the script. Note that `outputs` here
// is the output *count*, not the array the list endpoint returns.
export function getCollectorStatus(id) {
  return request(`/api/collectors/${encodeURIComponent(id)}/status`);
}

export function listCollectorRuns(id, { limit } = {}) {
  const query = limit ? `?limit=${encodeURIComponent(limit)}` : "";
  return request(`/api/collectors/${encodeURIComponent(id)}/runs${query}`);
}

// ── Device templates ────────────────────────────────────────────────
//
// A reusable Modbus device definition — a model's transport defaults and its
// complete register map — so connecting the second device of a model already
// commissioned is a host and a unit id rather than fifty retyped registers.
// `id` accepts a template name as well as an id. See docs/battex/
// 03-modbus-onboarding.md.

export function listDeviceTemplates() {
  return request("/api/device-templates");
}

export function getDeviceTemplate(id) {
  return request(`/api/device-templates/${encodeURIComponent(id)}`);
}

export function createDeviceTemplate(payload) {
  return request("/api/device-templates", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Every field of the PUT body is optional and an omitted one is left alone.
export function updateDeviceTemplate(id, patch) {
  return request(`/api/device-templates/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteDeviceTemplate(id) {
  return request(`/api/device-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// The supported way to change a builtin: the copy is yours and the startup seed
// never touches it.
export function cloneDeviceTemplate(id, name) {
  return request(`/api/device-templates/${encodeURIComponent(id)}/clone`, {
    method: "POST",
    body: JSON.stringify({ ...(name ? { name } : {}) }),
  });
}

// Derives the complete connector spec for one physical device and the tag
// declarations it needs. Deliberately does NOT preview: deriving a spec touches
// no device and allocates no revision, which is what keeps the review where it
// belongs. Answers {spec, tags, tag_folder, template_id}.
export function instantiateDeviceTemplate(id, instance) {
  return request(`/api/device-templates/${encodeURIComponent(id)}/instantiate`, {
    method: "POST",
    body: JSON.stringify(instance),
  });
}
